import type { Cell, Sheet } from "@fortune-sheet/core";
import type ExcelJS from "exceljs";
import { hexToArgb, numberToAlignment, numberToVerticalAlignment } from "./excelToFortuneSheet";

/**
 * Session-only cell formatting applied in a spreadsheet PREVIEW, expressed as a delta and replayed onto
 * both renderings of the workbook it came from.
 *
 * It is a delta rather than the edited sheets themselves for two reasons, both structural. The sheets
 * Fortune Sheet hands back carry no `celldata` (it builds the 2-D `data` matrix and deletes `celldata`),
 * and `generatePrintoutPdf` reads `celldata` exclusively — so feeding the edited sheets to the renderer
 * produces blank pages. And the workbook builder already sets bold on headers, fills on dividers and
 * alignments everywhere, while the round trip through Fortune Sheet is lossy (`ff` is forced to Calibri,
 * `argbToHex` drops the alpha byte, `fs` is absent unless the workbook set one) — so copying the edited
 * styles wholesale would flatten the builder's own work. A delta touches only what the user touched.
 */

/**
 * The cell-style keys the preview toolbar can change. The single list the toolbar, the diff and both
 * appliers derive from — adding a toolbar button means adding a key here, and nothing else has its own
 * copy of the set. `un` is underline and `cl` is Fortune Sheet's "cancel line", i.e. strikethrough.
 *
 * All but one key here is PAINT — it changes how a cell looks, never how much room it needs. `fs` is the
 * exception and the reason the PDF path rebuilds its workbook rather than painting the preview's sheets:
 * a larger font needs a taller row, the xlsx grows one through `applyWrapText`/`lineHeightForFontPt`
 * (33pt for 24pt type), and nothing downstream of the preview's already-computed `rowlen` can match that.
 * Rebuilding puts both outputs through that one pass, so `fs` is as safe here as the paint keys are.
 * Anything added to this list that moves a boundary inherits that guarantee only because of the rebuild —
 * see `sheetsForPdf` in `DailyPrintoutButton`, and the per-key parity matrix in
 * `printoutExcelPdfParity.spec.ts`, which fails if a key reaches one output and not the other.
 */
export const OVERLAY_STYLE_KEYS = ["bl", "it", "un", "cl", "fc", "bg", "fs", "ht", "vt"] as const;

export type OverlayStyleKey = (typeof OVERLAY_STYLE_KEYS)[number];

/**
 * The keys a user changed on one cell. A key PRESENT WITH `undefined` means "cleared" — the toolbar's
 * colour pickers have a "Reset colour" entry that removes the attribute rather than setting one — and is
 * applied as a removal by both appliers, so the preview and the exports agree.
 *
 * `ht`/`vt` are always normalised to numbers here even though the widget writes them as strings; see
 * `asAlignmentCode`.
 */
export type CellStyleDelta = Partial<Pick<Cell, OverlayStyleKey>>;

/**
 * Worksheet NAME → `"{row}_{col}"` → delta. An empty object means no formatting was applied, which is
 * the cheap "nothing to do" test both export paths make.
 *
 * Keyed by NAME, not by sheet id, and the distinction is the whole reason this type exists. Matching the
 * preview's edited sheets against the pristine ones happens inside one widget lifetime, where the `id`
 * `excelBufferToFortuneSheet` assigns is stable and unique. Matching the overlay against an ExcelJS
 * worksheet happens at download time, across a SECOND, independent build of the workbook — and a name is
 * the only identifier meaningful on both sides of that rebuild (Excel guarantees it unique within a
 * workbook). An ExcelJS `id` is an internal counter and is wrong to trust across builds.
 */
export type SheetStyleOverlay = Record<string, Record<string, CellStyleDelta>>;

const cellKey = (row: number, column: number): string => `${row}_${column}`;

/** A `"{row}_{col}"` key back to its two numbers. */
const parseCellKey = (key: string): { row: number; column: number } => {
	const [row, column] = key.split("_").map(Number);
	return { row, column };
};

/** Excel's 1-based row/column numbering against Fortune Sheet's 0-based. */
const TO_EXCEL_INDEX = 1;

/** Fortune Sheet writes `1` for an enabled boolean attribute and `0` for one the user switched off. */
const ATTR_ON = 1;

/**
 * The overlay keys whose value is an alignment CODE, which Fortune Sheet writes as a **string**.
 *
 * `updateFormatCell` maps "left"/"center"/"right" to `"1"`/`"0"`/`"2"` before writing, and the widget's
 * own renderer compares them as strings (`horizonAlign === "0"`). Our converter writes real numbers for
 * the same two attributes, so the two halves of a diff are not comparable until one side is coerced —
 * uncoerced, `"2" !== 2` records a bogus delta that then throws in `numberToAlignment`, taking the whole
 * download with it, and re-picking the alignment a cell already has produces an overlay out of nothing.
 */
const ALIGNMENT_KEYS: ReadonlySet<OverlayStyleKey> = new Set(["ht", "vt"]);

/**
 * An alignment code as a number, whichever way the widget spelled it. Coerced at the overlay BOUNDARY so
 * the overlay's own contract is "numbers" and neither applier nor the PDF renderer needs a string branch.
 *
 * Throws rather than passing a `NaN` on: a silently dropped alignment is invisible in review, and a `NaN`
 * written into a workbook is worse than a failed download.
 */
const asAlignmentCode = (key: OverlayStyleKey, value: unknown): number => {
	const code = Number(value);
	if (!Number.isFinite(code)) throw new Error(`Fortune Sheet gave a non-numeric ${key}: ${JSON.stringify(value)}`);
	return code;
};

/**
 * The overlay keys whose value is a hex colour, which the two sides spell in different CASE.
 *
 * The same split as `ALIGNMENT_KEYS`, for the same reason. `argbToHex` carries through the workbook's own
 * ARGB literals, which the printout builders write uppercase (`FFD3D3D3`), while Fortune Sheet can only
 * produce lowercase — its palette is lowercase and `<input type="color">` is spec'd to normalise to it.
 * Compared raw, re-picking the colour a cell ALREADY has records `"#d3d3d3" !== "#D3D3D3"` as a change:
 * harmless in the file (the same colour lands either way) but it flips the "formatting applied" notice on
 * and fires the analytics event for a no-op.
 */
const COLOR_KEYS: ReadonlySet<OverlayStyleKey> = new Set(["fc", "bg"]);

/** One overlay key's value in the spelling the CONVERTER would have produced, so the two sides of a diff
 *  are comparable. `undefined` stays `undefined` (= cleared). */
const normalizedStyleValue = (key: OverlayStyleKey, value: Cell[OverlayStyleKey]): Cell[OverlayStyleKey] => {
	if (value === undefined) return value;
	if (ALIGNMENT_KEYS.has(key)) return asAlignmentCode(key, value);
	// Upper, not lower: `argbToHex` and `hexToArgb` both speak uppercase, so this is the shape the rest of
	// the pipeline already uses rather than a third spelling invented here.
	if (COLOR_KEYS.has(key) && typeof value === "string") return value.toUpperCase();
	return value;
};

/** The cells a merge covers but does not start. `buildSheetBody` never draws one and ExcelJS shadows one
 *  with its master's style, so formatting recorded on one is at best invisible and at worst misleading —
 *  and Fortune Sheet writes to every cell of the selection rectangle, covered ones included. */
const mergeCoveredCells = (sheet: Sheet): Set<string> => {
	const covered = new Set<string>();
	for (const merge of Object.values(sheet.config?.merge ?? {})) {
		for (let dr = 0; dr < merge.rs; dr++) {
			for (let dc = 0; dc < merge.cs; dc++) {
				if (dr === 0 && dc === 0) continue;
				covered.add(cellKey(merge.r + dr, merge.c + dc));
			}
		}
	}
	return covered;
};

/**
 * The last row and column the ORIGINAL sheet actually occupies.
 *
 * Formatting a cell past it would make `worksheet.getCell(...)` extend the worksheet's used range, which
 * is exactly the corruption the printouts arch doc records: Excel scales fit-to-page over the extended
 * range and the PDF draws extra empty bordered columns. A user can select past the last real column and
 * click Fill, so this clamp is not theoretical.
 *
 * The populated cells ARE the used range; the declared size is only consulted to cap it, because
 * `excelBufferToFortuneSheet` falls back to 50 rows / 20 columns for a worksheet that reports none, and
 * that fallback is larger than the range. A sheet that declares no size at all is therefore clamped by
 * its populated cells alone — deliberately NOT treated as "size 0", which would silently discard the
 * whole sheet's formatting rather than merely failing to cap an already-correct bound.
 */
const usedRangeOf = (sheet: Sheet): { lastRow: number; lastColumn: number } => {
	let lastPopulatedRow = -1;
	let lastPopulatedColumn = -1;
	for (const entry of sheet.celldata ?? []) {
		if (entry.v == null) continue;
		lastPopulatedRow = Math.max(lastPopulatedRow, entry.r);
		lastPopulatedColumn = Math.max(lastPopulatedColumn, entry.c);
	}
	const declaredLastRow = sheet.row === undefined ? Number.POSITIVE_INFINITY : sheet.row - 1;
	const declaredLastColumn = sheet.column === undefined ? Number.POSITIVE_INFINITY : sheet.column - 1;
	return { lastRow: Math.min(lastPopulatedRow, declaredLastRow), lastColumn: Math.min(lastPopulatedColumn, declaredLastColumn) };
};

/** Everything about a pristine sheet the diff needs, none of which changes for the widget's lifetime. */
type OriginalIndex = { cells: Map<string, Cell>; covered: Set<string>; lastRow: number; lastColumn: number };

/**
 * Built once per pristine sheet. The widget's `onChange` fires on nearly every internal change — a scroll,
 * a selection — and each one would otherwise re-walk every cell of the workbook three times.
 *
 * A `WeakMap` keyed on the sheet object is exactly the right lifetime: the preview holds one referentially
 * stable array for as long as the widget lives, and a rebuild replaces it with new objects that the cache
 * then drops on its own.
 */
const originalIndexCache = new WeakMap<Sheet, OriginalIndex>();

const originalIndexOf = (sheet: Sheet): OriginalIndex => {
	const cached = originalIndexCache.get(sheet);
	if (cached) return cached;

	const cells = new Map<string, Cell>();
	for (const entry of sheet.celldata ?? []) {
		if (entry.v != null) cells.set(cellKey(entry.r, entry.c), entry.v);
	}
	const index: OriginalIndex = { cells, covered: mergeCoveredCells(sheet), ...usedRangeOf(sheet) };
	originalIndexCache.set(sheet, index);
	return index;
};

/**
 * Did the user run `clear-format` on this cell, as opposed to clearing ONE attribute?
 *
 * The two produce the same shape on the attribute itself — a key that was present and is now gone — so
 * the sentinel is a key OUTSIDE the overlay set: **`ff`**, the font family.
 * `handleClearFormat` reduces a cell to `_.pick(cell, "v", "m", "mc", "f", "ct")`, which drops `ff` along
 * with everything else, and no control this toolbar offers ever writes `ff` (the font-family combo is not
 * in `FORMATTING_TOOLBAR_ITEMS`). `excelBufferToFortuneSheet` writes `ff` on **every** cell it converts,
 * unconditionally, so the sentinel is always there to look at.
 *
 * Counting "did every overlay key vanish at once" was the obvious alternative and is ambiguous where it
 * matters: a cell whose only styling is one colour is indistinguishable from a clear-format by that test.
 *
 * A pristine cell with no `ff` at all cannot be judged — only a hand-built sheet produces one — so it is
 * read as a clear-format, the non-destructive direction: revert to the workbook rather than strip it.
 */
const wasClearFormatted = (original: Cell, edited: Cell): boolean => original.ff === undefined || edited.ff === undefined;

/**
 * The formatting one cell gained relative to the workbook's own, or `null` for a cell to leave alone.
 *
 * `null` covers "nothing changed" and `clear-format`. Per the feature's settled decision, `clear-format`
 * REVERTS the cell to whatever the workbook builder set rather than stripping it — dropping the cell from
 * the overlay is what achieves that — so a user can never accidentally un-bold the printout's own headers.
 * Reverting the whole cell, not just one key, is right because `clear-format` clears every key at once.
 *
 * A single attribute cleared on its own (the colour pickers' "Reset colour") is a different thing and is
 * recorded faithfully, as the key present with `undefined`; otherwise the preview would show the reset
 * while both exports kept the builder's colour.
 */
const cellDelta = (original: Cell | undefined, edited: Cell): CellStyleDelta | null => {
	const delta: CellStyleDelta = {};
	let changed = false;

	for (const key of OVERLAY_STYLE_KEYS) {
		const before = normalizedStyleValue(key, original?.[key]);
		const after = normalizedStyleValue(key, edited[key]);
		if (after === before) continue;
		// Present on the workbook's cell, gone from the user's: either a whole-cell clear or one reset.
		if (after === undefined && original && wasClearFormatted(original, edited)) return null;
		// `Pick` keeps each key's own type, which a loop variable cannot express without a cast per key.
		Object.assign(delta, { [key]: after });
		changed = true;
	}

	return changed ? delta : null;
};

/**
 * What the user formatted in the preview, as a replayable delta.
 *
 * `original` are the pristine sheets the preview was seeded with and `edited` the ones Fortune Sheet
 * reports back; they are matched by `id`, which is stable for the widget's lifetime, and the result is
 * re-keyed by worksheet name for the rebuild at download time.
 */
export const extractStyleOverlay = (original: Sheet[], edited: Sheet[]): SheetStyleOverlay => {
	const originalById = new Map(original.map((sheet) => [sheet.id, sheet]));
	const overlay: SheetStyleOverlay = {};

	for (const editedSheet of edited) {
		const originalSheet = originalById.get(editedSheet.id);
		// A sheet the preview was never seeded with has nothing to diff against, and guessing what the
		// workbook gave it would write the widget's own defaults into the export.
		if (!originalSheet) continue;

		const { cells: originalCells, covered, lastRow, lastColumn } = originalIndexOf(originalSheet);
		const data = editedSheet.data ?? [];
		const sheetDeltas: Record<string, CellStyleDelta> = {};

		for (let r = 0; r <= Math.min(lastRow, data.length - 1); r++) {
			const row = data[r];
			if (!row) continue;
			for (let c = 0; c <= Math.min(lastColumn, row.length - 1); c++) {
				const editedCell = row[c];
				if (!editedCell) continue;
				const key = cellKey(r, c);
				if (covered.has(key)) continue;
				const delta = cellDelta(originalCells.get(key), editedCell);
				if (delta) sheetDeltas[key] = delta;
			}
		}

		if (Object.keys(sheetDeltas).length > 0) overlay[originalSheet.name] = sheetDeltas;
	}

	return overlay;
};

/** Same key set, counting a key present with `undefined` — the whole point of this comparison. */
const sameKeys = (a: object, b: object): boolean => {
	const keys = Object.keys(a);
	return keys.length === Object.keys(b).length && keys.every((key) => key in b);
};

const sameDelta = (a: CellStyleDelta, b: CellStyleDelta): boolean => sameKeys(a, b) && (Object.keys(a) as OverlayStyleKey[]).every((key) => a[key] === b[key]);

/**
 * Whether two overlays express the same formatting.
 *
 * Structural rather than `JSON.stringify` equality, which is what this replaced and which was wrong in
 * one specific way: serialising DROPS a key present with `undefined`, and that is precisely how a
 * cleared attribute is spelled here. So `{ fc: undefined }` and `{ fc: undefined, bg: undefined }` both
 * serialised to `{}` — a SECOND reset on an already-reset cell read as "no change", never reached state,
 * and never reached the export, which then kept the builder's own fill.
 */
export const styleOverlaysEqual = (a: SheetStyleOverlay, b: SheetStyleOverlay): boolean =>
	sameKeys(a, b) &&
	Object.keys(a).every((sheet) => sameKeys(a[sheet], b[sheet]) && Object.keys(a[sheet]).every((cell) => sameDelta(a[sheet][cell], b[sheet][cell])));

/** Excel's "no fill", for a cell whose fill the user reset. */
const NO_FILL: ExcelJS.FillPattern = { type: "pattern", pattern: "none" };

/**
 * Fortune Sheet's `un`/`cl` as the ExcelJS font flags they mean.
 *
 * Tested with `in` rather than `!== undefined` throughout: a key present with `undefined` means the user
 * CLEARED it ("Reset colour"), which has to remove the attribute, not be skipped as "unchanged".
 */
const applyFontDelta = (cell: ExcelJS.Cell, delta: CellStyleDelta): void => {
	const font: Partial<ExcelJS.Font> = { ...cell.font };
	if ("bl" in delta) font.bold = delta.bl === ATTR_ON;
	if ("it" in delta) font.italic = delta.it === ATTR_ON;
	if ("un" in delta) font.underline = delta.un === ATTR_ON;
	if ("cl" in delta) font.strike = delta.cl === ATTR_ON;
	// Cleared back to the workbook's own size rather than to a number of our choosing: ExcelJS reads an
	// absent `size` as "the workbook default", which is what the builder wrote the cell at.
	if ("fs" in delta) {
		if (delta.fs === undefined) delete font.size;
		else font.size = delta.fs;
	}
	if ("fc" in delta) {
		if (delta.fc === undefined) delete font.color;
		else font.color = { argb: hexToArgb(delta.fc) };
	}
	cell.font = font;
};

/**
 * The overlay replayed onto the workbook the xlsx download hands over. Mutates in place, like every other
 * ExcelJS pass in `downloads/`, and takes the WORKBOOK rather than its bytes on purpose: ExcelJS is
 * documented in the printouts arch doc as lossy on read (a file's `rowBreaks` come back empty), so a
 * `load()` → `writeBuffer()` round trip would silently drop anything else with the same defect.
 */
export const applyStyleOverlayToWorkbook = (workbook: ExcelJS.Workbook, overlay: SheetStyleOverlay): void => {
	for (const [sheetName, deltas] of Object.entries(overlay)) {
		const worksheet = workbook.getWorksheet(sheetName);
		// A worksheet the rebuild no longer produces (a setting changed between preview and download, or a
		// tab that this day has no data for) simply has nothing to format.
		if (!worksheet) continue;

		for (const [key, delta] of Object.entries(deltas)) {
			const { row, column } = parseCellKey(key);
			const cell = worksheet.getCell(row + TO_EXCEL_INDEX, column + TO_EXCEL_INDEX);

			applyFontDelta(cell, delta);

			if ("bg" in delta) {
				cell.fill = delta.bg === undefined ? NO_FILL : { type: "pattern", pattern: "solid", fgColor: { argb: hexToArgb(delta.bg) } };
			}
			// MERGED, never replaced: the builder's own `wrapText` lives on this object, and dropping it is
			// the exact bug `applyWrapText`'s header comment warns about.
			if ("ht" in delta) {
				cell.alignment = { ...cell.alignment, horizontal: delta.ht === undefined ? undefined : numberToAlignment(delta.ht) };
			}
			if ("vt" in delta) {
				cell.alignment = { ...cell.alignment, vertical: delta.vt === undefined ? undefined : numberToVerticalAlignment(delta.vt) };
			}
		}
	}
};
